import React, { useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import az from 'date-fns/locale/az';
import api from "../../services/api";
import { HeyatYoluContext } from "../../App";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import "./styles/CreateProfile.css";

registerLocale('az', az);
setDefaultLocale('az');

const STORAGE_KEY = 'heyatyolu_draft_profile';

const FILE_VALIDATION = {
  MAX_SIZE: 10 * 1024 * 1024,
  MAX_MEDIA_FILES: 10,
  MAX_BIO_LENGTH: 2000,
  ALLOWED_IMAGES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_VIDEOS: ['video/mp4', 'video/avi', 'video/mov', 'video/webm'],
  get ALLOWED_MEDIA() {
    return [...this.ALLOWED_IMAGES, ...this.ALLOWED_VIDEOS];
  }
};

const VALIDATION_MESSAGES = {
  REQUIRED_FIELD: "Bu sahə mütləqdir",
  FILE_TOO_LARGE: "Fayl ölçüsü 10MB-dan böyük ola bilməz",
  INVALID_IMAGE_TYPE: "Profil şəkli yalnız JPEG, PNG, GIF və WEBP formatında ola bilər",
  INVALID_MEDIA_TYPE: "Yalnız şəkil (JPEG, PNG, GIF, WEBP) və video (MP4, AVI, MOV, WEBM) faylları qəbul edilir",
  DATE_VALIDATION: "Doğum tarixi vəfat tarixindən sonra ola bilməz",
  MAX_MEDIA_FILES: `Maksimum ${FILE_VALIDATION.MAX_MEDIA_FILES} fayl yükləyə bilərsiniz`,
  MARTYR_DOCUMENT_REQUIRED: "Şəhidliyi təsdiqləyən sənəd mütləqdir",
  MOURNER_INCOMPLETE: "məlumatları tam doldurulmayıb"
};

function CreateProfile() {
  const navigate = useNavigate();
  // ← DƏYİŞDİ: paymentEnabled əlavə olundu
  const { religions, relationTypes, paymentAmount, paymentEnabled } = useContext(HeyatYoluContext);

  const mediaPreviewUrls = useRef(new Set());
  const photoPreviewUrl = useRef(null);
  const autoSaveTimeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: "",
    fatherName: "",
    lastName: "",
    dateOfBirth: null,
    dateOfDeath: null,
    religionId: "",
    birthPlace: "",
    deathPlace: "",
    biography: "",
  });

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [martyrDocument, setMartyrDocument] = useState(null);

  const [mourners, setMourners] = useState([{ relationTypeId: "", fullName: "" }]);
  const [isMartyr, setIsMartyr] = useState(false);
  const [wantsCeremonies, setWantsCeremonies] = useState(false);
  const [ceremonies, setCeremonies] = useState({
    threeDay: { active: false, date: null, location: "", mapLink: "" },
    sevenDay: { active: false, date: null, location: "", mapLink: "" },
    fortyDay: { active: false, date: null, location: "", mapLink: "" },
    anniversary: { active: false, date: null, location: "", mapLink: "" }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showBioTip, setShowBioTip] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);

  const cleanupPreviewUrl = useCallback((url) => {
    if (url && typeof url === 'string') {
      URL.revokeObjectURL(url);
      mediaPreviewUrls.current.delete(url);
    }
  }, []);

  const cleanupAllPreviews = useCallback(() => {
    if (photoPreviewUrl.current) {
      URL.revokeObjectURL(photoPreviewUrl.current);
      photoPreviewUrl.current = null;
    }

    mediaPreviewUrls.current.forEach(url => {
      URL.revokeObjectURL(url);
    });
    mediaPreviewUrls.current.clear();
  }, []);

  const validateFile = useCallback((file, isProfilePhoto = false) => {
    if (!file) return "Fayl seçilməyib";

    if (file.size > FILE_VALIDATION.MAX_SIZE) {
      return VALIDATION_MESSAGES.FILE_TOO_LARGE;
    }

    if (isProfilePhoto && !FILE_VALIDATION.ALLOWED_IMAGES.includes(file.type)) {
      return VALIDATION_MESSAGES.INVALID_IMAGE_TYPE;
    }

    if (!isProfilePhoto && !FILE_VALIDATION.ALLOWED_MEDIA.includes(file.type)) {
      return VALIDATION_MESSAGES.INVALID_MEDIA_TYPE;
    }

    return null;
  }, []);

  const sanitizeInput = useCallback((input) => {
    if (!input || typeof input !== 'string') return input;
    return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }, []);

  const saveDraft = useCallback(() => {
    try {
      const draftData = {
        formData: {
          ...formData,
          dateOfBirth: formData.dateOfBirth?.toISOString(),
          dateOfDeath: formData.dateOfDeath?.toISOString(),
        },
        mourners,
        isMartyr,
        wantsCeremonies,
        ceremonies: Object.entries(ceremonies).reduce((acc, [key, value]) => {
          acc[key] = {
            ...value,
            date: value.date?.toISOString() || null
          };
          return acc;
        }, {}),
        timestamp: new Date().toISOString()
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(draftData));
    } catch (error) {
      console.error("Draft saxlanılarkən xəta:", error);
    }
  }, [formData, mourners, isMartyr, wantsCeremonies, ceremonies]);

  const loadDraft = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return false;

      const draftData = JSON.parse(saved);

      setFormData({
        ...draftData.formData,
        dateOfBirth: draftData.formData.dateOfBirth
          ? new Date(draftData.formData.dateOfBirth)
          : null,
        dateOfDeath: draftData.formData.dateOfDeath
          ? new Date(draftData.formData.dateOfDeath)
          : null,
      });

      if (draftData.mourners?.length) {
        setMourners(draftData.mourners);
      }

      setIsMartyr(draftData.isMartyr || false);
      setWantsCeremonies(draftData.wantsCeremonies || false);

      if (draftData.ceremonies) {
        const restoredCeremonies = Object.entries(draftData.ceremonies).reduce((acc, [key, value]) => {
          acc[key] = {
            ...value,
            date: value.date ? new Date(value.date) : null
          };
          return acc;
        }, {});
        setCeremonies(restoredCeremonies);
      }

      toast.info("Yadda saxlanılmış məlumatlar yükləndi");
      return true;
    } catch (error) {
      console.error("Draft yüklənərkən xəta:", error);
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
  }, []);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setShowDraftModal(false);
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!sanitizeInput(formData.firstName)) {
      errors.firstName = "Ad sahəsi mütləqdir";
    }

    if (!sanitizeInput(formData.fatherName)) {
      errors.fatherName = "Ata adı sahəsi mütləqdir";
    }

    if (!sanitizeInput(formData.lastName)) {
      errors.lastName = "Soyad sahəsi mütləqdir";
    }

    if (!formData.dateOfBirth) {
      errors.dateOfBirth = "Doğum tarixi mütləqdir";
    }

    if (!formData.dateOfDeath) {
      errors.dateOfDeath = "Vəfat tarixi mütləqdir";
    }

    if (!formData.religionId) {
      errors.religionId = "Din seçimi mütləqdir";
    }

    if (!sanitizeInput(formData.birthPlace)) {
      errors.birthPlace = "Doğum yeri mütləqdir";
    }

    if (!sanitizeInput(formData.deathPlace)) {
      errors.deathPlace = "Dəfn yeri mütləqdir";
    }

    if (!sanitizeInput(formData.biography)) {
      errors.biography = "Bioqrafiya mütləqdir";
    }

    if (!photo) {
      errors.photo = "Profil şəkli mütləqdir";
    }

    if (isMartyr && !martyrDocument) {
      errors.martyrDocument = VALIDATION_MESSAGES.MARTYR_DOCUMENT_REQUIRED;
    }

    if (formData.dateOfBirth && formData.dateOfDeath) {
      if (new Date(formData.dateOfBirth) > new Date(formData.dateOfDeath)) {
        errors.dateValidation = VALIDATION_MESSAGES.DATE_VALIDATION;
      }
    }

    mourners.forEach((mourner, index) => {
      if (!mourner.relationTypeId || !sanitizeInput(mourner.fullName)) {
        errors[`mourner_${index}`] = `${index + 1}-ci yas tutanın ${VALIDATION_MESSAGES.MOURNER_INCOMPLETE}`;
      }
    });

    return errors;
  }, [formData, photo, isMartyr, martyrDocument, mourners, sanitizeInput]);

  const availableCeremonies = useMemo(() => {
    if (!formData.dateOfDeath) {
      return {
        threeDay: false,
        sevenDay: false,
        fortyDay: false,
        anniversary: false
      };
    }

    const today = new Date();
    const deathDate = new Date(formData.dateOfDeath);
    const diffDays = Math.floor((today - deathDate) / (1000 * 60 * 60 * 24));

    return {
      threeDay: diffDays <= 5,
      sevenDay: diffDays <= 10,
      fortyDay: diffDays <= 45,
      anniversary: diffDays <= 370
    };
  }, [formData.dateOfDeath]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);

    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [formErrors, sanitizeInput]);

  const handleDateChange = useCallback((field) => (date) => {
    setFormData(prev => ({ ...prev, [field]: date }));

    if (formErrors[field] || formErrors.dateValidation) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null,
        dateValidation: null
      }));
    }
  }, [formErrors]);

  const handlePhotoChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const error = validateFile(file, true);
    if (error) {
      toast.error(error);
      return;
    }

    if (photoPreviewUrl.current) {
      URL.revokeObjectURL(photoPreviewUrl.current);
    }

    const previewUrl = URL.createObjectURL(file);
    photoPreviewUrl.current = previewUrl;

    setPhoto(file);
    setPhotoPreview(previewUrl);

    if (formErrors.photo) {
      setFormErrors(prev => ({ ...prev, photo: null }));
    }
  }, [validateFile, formErrors.photo]);

  const handleMediaChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);

    if (mediaFiles.length + files.length > FILE_VALIDATION.MAX_MEDIA_FILES) {
      toast.error(VALIDATION_MESSAGES.MAX_MEDIA_FILES);
      return;
    }

    const validFiles = [];

    files.forEach(file => {
      const error = validateFile(file, false);
      if (error) {
        toast.error(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
        const previewUrl = URL.createObjectURL(file);
        file.previewUrl = previewUrl;
        mediaPreviewUrls.current.add(previewUrl);
      }
    });

    if (validFiles.length > 0) {
      setMediaFiles(prev => [...prev, ...validFiles]);
    }

    e.target.value = '';
  }, [mediaFiles.length, validateFile]);

  const handleMediaRemove = useCallback((index) => {
    setMediaFiles(prev => {
      const fileToRemove = prev[index];
      if (fileToRemove?.previewUrl) {
        cleanupPreviewUrl(fileToRemove.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, [cleanupPreviewUrl]);

  const handleMournerChange = useCallback((index, field, value) => {
    const sanitizedValue = field === 'fullName' ? sanitizeInput(value) : value;

    setMourners(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: sanitizedValue };
      return updated;
    });

    if (formErrors[`mourner_${index}`]) {
      setFormErrors(prev => ({ ...prev, [`mourner_${index}`]: null }));
    }
  }, [formErrors, sanitizeInput]);

  const addMournerField = useCallback(() => {
    setMourners(prev => [...prev, { relationTypeId: "", fullName: "" }]);
  }, []);

  const removeMournerField = useCallback((index) => {
    if (mourners.length > 1) {
      setMourners(prev => prev.filter((_, i) => i !== index));

      if (formErrors[`mourner_${index}`]) {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`mourner_${index}`];
          return newErrors;
        });
      }
    }
  }, [mourners.length, formErrors]);

  const handleCeremonyChange = useCallback((key, field, value) => {
    const sanitizedValue = field === 'location' ? sanitizeInput(value) : value;

    setCeremonies(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: sanitizedValue }
    }));
  }, [sanitizeInput]);

  const resetForm = useCallback(() => {
    setFormData({
      firstName: "",
      fatherName: "",
      lastName: "",
      dateOfBirth: null,
      dateOfDeath: null,
      religionId: "",
      birthPlace: "",
      deathPlace: "",
      biography: "",
    });

    cleanupAllPreviews();

    setPhoto(null);
    setPhotoPreview(null);
    setMediaFiles([]);
    setMartyrDocument(null);

    setMourners([{ relationTypeId: "", fullName: "" }]);
    setIsMartyr(false);
    setWantsCeremonies(false);
    setCeremonies({
      threeDay: { active: false, date: null, location: "", mapLink: "" },
      sevenDay: { active: false, date: null, location: "", mapLink: "" },
      fortyDay: { active: false, date: null, location: "", mapLink: "" },
      anniversary: { active: false, date: null, location: "", mapLink: "" },
    });

    setFormErrors({});
    setShowBioTip(false);

    clearDraft();
  }, [cleanupAllPreviews, clearDraft]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      toast.warning("Zəhmət olmasa gözləyin, profil yaradılır...");
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Xətalı sahələri düzəltdikdən sonra yenidən cəhd edin");
      return;
    }

    setIsSubmitting(true);

    try {
      const formatDateOnly = (date) => date.toLocaleDateString("en-CA");

      const data = new FormData();

      data.append("FirstName", formData.firstName);
      data.append("FatherName", formData.fatherName);
      data.append("LastName", formData.lastName);
      data.append("DateOfBirth", formatDateOnly(formData.dateOfBirth));
      data.append("DateOfDeath", formatDateOnly(formData.dateOfDeath));
      data.append("ReligionId", formData.religionId);
      data.append("Biography", formData.biography || "");
      data.append("IsMartyr", isMartyr);
      data.append("Photo", photo);
      data.append("BirthPlace", formData.birthPlace);
      data.append("DeathPlace", formData.deathPlace);

      mourners.forEach((m, i) => {
        data.append(`Mourners[${i}].RelationTypeId`, m.relationTypeId);
        data.append(`Mourners[${i}].FullName`, m.fullName);
      });

      if (isMartyr && martyrDocument) {
        data.append("MartyrDocument", martyrDocument);
      }

      mediaFiles.forEach((file) => data.append("Media", file));

      const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
      Object.entries(ceremonies).forEach(([key, value]) => {
        if (value.active) {
          if (value.date) {
            data.append(`${capitalize(key)}.Date`, formatDateOnly(value.date));
          }
          if (value.location) {
            data.append(`${capitalize(key)}.Location`, value.location);
          }
          if (value.mapLink) {
            data.append(`${capitalize(key)}.MapLink`, value.mapLink);
          }
        }
      });

      // ── 1. Profili yarat ──
      const res = await api.post("/memorialprofile/create", data);
      const profileId = res.data.id;
      const requiresPayment = res.data.requiresPayment; // ← YENİ: server qərar verir

      // ── 2. Ödəniş tələb olunmursa (şəhid VƏ YA pulsuz rejim) ──  ← DƏYİŞDİ
      if (!requiresPayment) {
        toast.success("Profil uğurla yaradıldı! Admin təsdiqini gözləyir.");
        resetForm();
        navigate(`/merhum/${profileId}`);
        return;
      }

      // ── 3. Ödəniş sifarişi yarat (BİR DƏFƏ, döngü yoxdur) ──
      let payRes;
      try {
        payRes = await api.post("/payment/create-payriff-order", { profileId });
      } catch (payErr) {
        console.error("Payment error:", payErr.response?.data || payErr);
        toast.error(
          "Ödəniş sistemi hazırda əlçatan deyil. Profiliniz saxlanıldı — 'Profillərim' bölməsindən ödənişi tamamlaya bilərsiniz.",
          { autoClose: 7000 }
        );
        clearDraft();
        navigate("/kabinetim");
        return;
      }

      const paymentUrl = payRes.data?.paymentUrl;
      if (!paymentUrl) {
        toast.error("Ödəniş linki alınmadı. Zəhmət olmasa yenidən cəhd edin.");
        clearDraft();
        navigate("/kabinetim");
        return;
      }

      // ── 4. Ödəniş səhifəsinə yönləndir ──
      clearDraft();
      window.location.href = paymentUrl;

    } catch (err) {
      console.error("Profile creation error:", err.response?.data || err);
      const errorMessage = err.response?.data?.message || "Profil yaradılarkən xəta baş verdi";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    validateForm,
    formData,
    photo,
    mourners,
    isMartyr,
    martyrDocument,
    mediaFiles,
    ceremonies,
    resetForm,
    clearDraft,
    navigate
  ]);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const draftData = JSON.parse(saved);
        const draftDate = new Date(draftData.timestamp);
        const daysSinceDraft = (new Date() - draftDate) / (1000 * 60 * 60 * 24);

        if (daysSinceDraft <= 7) {
          setShowDraftModal(true);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error("Draft yoxlanılarkən xəta:", error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    const hasContent = formData.firstName ||
      formData.biography ||
      mourners.some(m => m.fullName) ||
      formData.lastName ||
      formData.fatherName;

    if (hasContent && !showDraftModal && !isSubmitting) {
      autoSaveTimeoutRef.current = setTimeout(() => {
        saveDraft();
      }, 3000);
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [formData, mourners, isMartyr, wantsCeremonies, ceremonies, saveDraft, showDraftModal, isSubmitting]);

  useEffect(() => {
    return () => {
      cleanupAllPreviews();
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [cleanupAllPreviews]);

  // RENDER FUNCTIONS
  const renderDraftModal = () => (
    showDraftModal && (
      <div className="cp-draft-modal-overlay" role="dialog" aria-modal="true">
        <div className="cp-draft-modal">
          <h3>📝 Yadda saxlanılmış məlumat tapıldı</h3>
          <p>Əvvəlki doldurulmuş formaya davam etmək istəyirsiniz?</p>
          <p className="cp-draft-note">
            <small>⚠️ Qeyd: Yüklənmiş fayllar (şəkil, video, sənəd) saxlanmır və yenidən yükləmək lazımdır.</small>
          </p>

          <div className="cp-draft-modal-actions">
            <button
              type="button"
              className="cp-btn-primary"
              onClick={() => {
                loadDraft();
                setShowDraftModal(false);
              }}
            >
              ✅ Davam et
            </button>

            <button
              type="button"
              className="cp-btn-secondary"
              onClick={() => {
                clearDraft();
                toast.info("Yeni forma başladılır");
              }}
            >
              🆕 Yeni forma başlat
            </button>
          </div>
        </div>
      </div>
    )
  );

  const renderPersonalInfo = () => (
    <div className="cp-form-section">
      <h3>Şəxsi Məlumatlar</h3>

      <label className={formErrors.firstName ? 'cp-error' : ''}>
        Ad *
        <input
          type="text"
          name="firstName"
          value={formData.firstName}
          placeholder="Mərhumun adı"
          onChange={handleInputChange}
          maxLength="50"
          required
        />
        {formErrors.firstName && (
          <span className="cp-error-message">{formErrors.firstName}</span>
        )}
      </label>

      <label className={formErrors.fatherName ? 'cp-error' : ''}>
        Ata adı *
        <input
          type="text"
          name="fatherName"
          value={formData.fatherName}
          placeholder="Mərhumun ata adı"
          onChange={handleInputChange}
          maxLength="50"
          required
        />
        {formErrors.fatherName && (
          <span className="cp-error-message">{formErrors.fatherName}</span>
        )}
      </label>

      <label className={formErrors.lastName ? 'cp-error' : ''}>
        Soyad *
        <input
          type="text"
          name="lastName"
          value={formData.lastName}
          placeholder="Mərhumun soyadı"
          onChange={handleInputChange}
          maxLength="50"
          required
        />
        {formErrors.lastName && (
          <span className="cp-error-message">{formErrors.lastName}</span>
        )}
      </label>

      <label className={formErrors.dateOfBirth ? 'cp-error' : ''}>
        Doğum tarixi *
        <DatePicker
          selected={formData.dateOfBirth}
          onChange={handleDateChange('dateOfBirth')}
          dateFormat="dd-MM-yyyy"
          placeholderText="Tarixi seçin"
          className="cp-date-input"
          locale="az"
          required
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          maxDate={new Date()}
          yearDropdownItemNumber={100}
        />
        {formErrors.dateOfBirth && (
          <span className="cp-error-message">{formErrors.dateOfBirth}</span>
        )}
      </label>

      <label className={formErrors.dateOfDeath ? 'cp-error' : ''}>
        Vəfat tarixi *
        <DatePicker
          selected={formData.dateOfDeath}
          onChange={handleDateChange('dateOfDeath')}
          dateFormat="dd-MM-yyyy"
          placeholderText="Tarixi seçin"
          className="cp-date-input"
          locale="az"
          required
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          maxDate={new Date()}
          minDate={formData.dateOfBirth}
          yearDropdownItemNumber={100}
        />
        {formErrors.dateOfDeath && (
          <span className="cp-error-message">{formErrors.dateOfDeath}</span>
        )}
        {formErrors.dateValidation && (
          <span className="cp-error-message">{formErrors.dateValidation}</span>
        )}
      </label>

      <label className={formErrors.birthPlace ? 'cp-error' : ''}>
        Doğum yeri *
        <input
          type="text"
          name="birthPlace"
          value={formData.birthPlace}
          placeholder="Nümunə: Bərdə rayon Qazaxlar kəndi"
          onChange={handleInputChange}
          maxLength="100"
          required
        />
        {formErrors.birthPlace && (
          <span className="cp-error-message">{formErrors.birthPlace}</span>
        )}
      </label>

      <label className={formErrors.deathPlace ? 'cp-error' : ''}>
        Dəfn olduğu yer *
        <input
          type="text"
          name="deathPlace"
          value={formData.deathPlace}
          placeholder="Nümunə: Səbail rayonu Bibiheybət qəbirsanlığı"
          onChange={handleInputChange}
          maxLength="100"
          required
        />
        {formErrors.deathPlace && (
          <span className="cp-error-message">{formErrors.deathPlace}</span>
        )}
      </label>

      <label className={formErrors.religionId ? 'cp-error' : ''}>
        Dini *
        <select
          name="religionId"
          value={formData.religionId}
          onChange={handleInputChange}
          required
        >
          <option value="">-- Seçin --</option>
          {religions.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
        {formErrors.religionId && (
          <span className="cp-error-message">{formErrors.religionId}</span>
        )}
      </label>
    </div>
  );

  const renderMournersSection = () => (
    <div className="cp-form-section">
      <h3>Yas Tutanlar</h3>
      {mourners.map((mourner, index) => (
        <div key={index} className={`cp-mourner-row ${formErrors[`mourner_${index}`] ? 'cp-error' : ''}`}>
          <select
            value={mourner.relationTypeId}
            onChange={(e) => handleMournerChange(index, "relationTypeId", e.target.value)}
            required
          >
            <option value="">-- Əlaqə növü --</option>
            {relationTypes.map((type) => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Adı və Soyadı"
            value={mourner.fullName}
            onChange={(e) => handleMournerChange(index, "fullName", e.target.value)}
            maxLength="100"
            required
          />

          {mourners.length > 1 && (
            <button
              type="button"
              onClick={() => removeMournerField(index)}
              className="cp-remove-btn"
              title="Sil"
            >
              ✕
            </button>
          )}

          {formErrors[`mourner_${index}`] && (
            <span className="cp-error-message cp-full-width">{formErrors[`mourner_${index}`]}</span>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={addMournerField}
        className="cp-add-btn"
      >
        ➕ Yas tutanı əlavə et
      </button>
    </div>
  );

  const renderBiographySection = () => (
    <div className="cp-form-section">
      <label className={formErrors.biography ? 'cp-error' : ''}>
        <div className="cp-label-with-info">
          Bioqrafiya *
          <button
            type="button"
            className="cp-info-icon"
            onClick={() => setShowBioTip(!showBioTip)}
          >
            ℹ️
          </button>
        </div>
        <textarea
          name="biography"
          value={formData.biography}
          placeholder="Məsələn: 1950-ci ildə Şamaxıda anadan olub. Müəllim kimi çalışıb. Ailəli idi, 3 övladı var. Hər kəsin hörmətini qazanmış bir insan idi."
          onChange={handleInputChange}
          maxLength={FILE_VALIDATION.MAX_BIO_LENGTH}
          rows="5"
          required
        />
        <small className="cp-char-counter">
          {formData.biography.length}/{FILE_VALIDATION.MAX_BIO_LENGTH} simvol
        </small>
        {formErrors.biography && (
          <span className="cp-error-message">{formErrors.biography}</span>
        )}
      </label>

      {showBioTip && (
        <div className="cp-info-panel">
          <p><strong>Bioqrafiya haqqında:</strong></p>
          <p>Burada mərhumun həyat yolu, təhsili, peşəsi, xidmətləri və digər ictimai fəaliyyətləri haqqında məlumat verə bilərsiniz.</p>
        </div>
      )}
    </div>
  );

  const renderPhotoSection = () => (
    <div className="cp-form-section">
      <h3>📷 Profil Şəkli *</h3>
      <div className={`cp-photo-upload ${formErrors.photo ? 'cp-error' : ''}`}>
        <label className="cp-file-label">
          {photoPreview ? (
            <div className="cp-photo-preview">
              <img src={photoPreview} alt="Profil şəkli" />
              <div className="cp-overlay">Dəyişmək üçün klikləyin</div>
            </div>
          ) : (
            <div className="cp-upload-placeholder">
              <span className="cp-upload-icon">📷</span>
              <span>Profil şəkli seçin *</span>
              <small>JPEG, PNG, GIF, WEBP (Max: 10MB)</small>
            </div>
          )}
          <input
            type="file"
            accept={FILE_VALIDATION.ALLOWED_IMAGES.join(',')}
            onChange={handlePhotoChange}
            hidden
          />
        </label>

        {photo && (
          <button
            type="button"
            onClick={() => {
              setPhoto(null);
              if (photoPreviewUrl.current) {
                URL.revokeObjectURL(photoPreviewUrl.current);
                photoPreviewUrl.current = null;
                setPhotoPreview(null);
              }
            }}
            className="cp-remove-photo-btn"
          >
            ❌ Şəkli sil
          </button>
        )}

        {formErrors.photo && (
          <span className="cp-error-message">{formErrors.photo}</span>
        )}
      </div>
    </div>
  );

  const renderMediaSection = () => (
    <div className="cp-form-section">
      <h3>📁 Əlavə Şəkil və Video</h3>
      <span className="cp-optional-badge">İstəyə Uyğun boş buraxıla bilər</span>
      <label className="cp-file-label cp-media-upload">
        <div className="cp-upload-area">
          <span className="cp-upload-icon">📁</span>
          <span>Əlavə şəkil və video seçin</span>
          <small>İstəyə uyğun - boş buraxıla bilər</small>
          <small>JPEG, PNG, GIF, WEBP, MP4, AVI, MOV, WEBM (Max: 10MB hər fayl)</small>
          <small>Maksimum {FILE_VALIDATION.MAX_MEDIA_FILES} fayl</small>
        </div>
        <input
          type="file"
          multiple
          accept={FILE_VALIDATION.ALLOWED_MEDIA.join(',')}
          onChange={handleMediaChange}
          hidden
        />
      </label>

      {mediaFiles.length > 0 && (
        <div className="cp-media-preview">
          <p className="cp-media-count">
            {mediaFiles.length}/{FILE_VALIDATION.MAX_MEDIA_FILES} fayl seçildi
          </p>
          <Slider
            className="cp-media-slider"
            infinite={false}
            speed={500}
            slidesToShow={Math.min(mediaFiles.length, 3)}
            slidesToScroll={1}
            responsive={[
              {
                breakpoint: 768,
                settings: { slidesToShow: Math.min(mediaFiles.length, 2) }
              },
              {
                breakpoint: 480,
                settings: { slidesToShow: 1 }
              }
            ]}
          >
            {mediaFiles.map((file, index) => {
              const isImage = FILE_VALIDATION.ALLOWED_IMAGES.includes(file.type);
              const previewUrl = file.previewUrl;

              return (
                <div key={`media-${index}`} className="cp-media-item">
                  <div className="cp-media-container">
                    {isImage ? (
                      <img
                        src={previewUrl}
                        alt={`Media ${index + 1}`}
                        loading="lazy"
                      />
                    ) : (
                      <video
                        src={previewUrl}
                        controls
                        preload="metadata"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => handleMediaRemove(index)}
                      className="cp-remove-media-btn"
                      title="Sil"
                    >
                      ×
                    </button>
                    <div className="cp-file-info">
                      <small>{file.name}</small>
                      <small>{(file.size / 1024 / 1024).toFixed(1)} MB</small>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      )}
    </div>
  );

  const renderMartyrSection = () => (
    <div className="cp-form-section">
      <div className="cp-martyr-section">
        <div className="cp-martyr-toggle-wrapper">
          <div className="cp-martyr-info-text">
            <span className="cp-martyr-icon">🕊️</span>
        <div className="cp-martyr-details">
              <strong>Bu şəxs şəhid olub</strong>
              {paymentEnabled ? (
                <small>Şəhidlər üçün paylaşım ödənişsizdir</small>
              ) : (
                <small>Şəhid profilləri xüsusi dizayn və status nişanı ilə yayımlanır</small>
              )}
            </div>
          </div>

          <div className="cp-switch-container">
            <input
              type="checkbox"
              id="martyr-switch"
              checked={isMartyr}
              onChange={(e) => setIsMartyr(e.target.checked)}
              className="cp-switch-input"
            />
            <label htmlFor="martyr-switch" className="cp-switch-label">
              <span className="cp-switch-slider"></span>
              <span className="cp-switch-text">{isMartyr ? 'Seçildi' : 'Seçin'}</span>
            </label>
          </div>
        </div>

        {isMartyr && (
          <div className={`cp-martyr-document ${formErrors.martyrDocument ? 'cp-error' : ''}`}>
            <label className="cp-file-label">
              <div className="cp-upload-area">
                <span className="cp-upload-icon">📎</span>
                <span>Şəhidliyi təsdiqləyən hər hansı sənədin şəklini yükləyin *</span>
                <small>
                  ✅ Bu sənəd admin tərəfindən yoxlama və təsdiq üçün tələb olunur.
                </small>
                <small>
                  Şəhidlik vəsiqəsi/sənədi, medal şəkli, arayış və ya şəhidliyi göstərən digər rəsmi materialın fotosunu yükləyin —
                  admin həmin şəxsin şəhid olduğunu bu məlumatla təsdiqləyəcək.
                </small>
              </div>
              <input
                type="file"
                accept={FILE_VALIDATION.ALLOWED_IMAGES.join(',')}
                onChange={(e) => setMartyrDocument(e.target.files?.[0] || null)}
                hidden
              />
            </label>
            {martyrDocument && (
              <p className="cp-file-name">
                📄 {martyrDocument.name} ({(martyrDocument.size / 1024 / 1024).toFixed(1)} MB)
              </p>
            )}
            {formErrors.martyrDocument && (
              <span className="cp-error-message">{formErrors.martyrDocument}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderCeremoniesSection = () => (
    Object.values(availableCeremonies).some(v => v) && (
      <div className="cp-form-section">
        <div className="cp-ceremony-section">
          <label className={`cp-ceremony-toggle ${wantsCeremonies ? 'cp-active' : ''}`}>
            <input
              type="checkbox"
              checked={wantsCeremonies}
              onChange={(e) => setWantsCeremonies(e.target.checked)}
            />
            <div className="cp-ceremony-content">
              <span className="cp-ceremony-icon">🕯️</span>
              <div className="cp-ceremony-text">
                <strong>Mərasim tarixlərini qeyd etmək istəyirsiniz?</strong>
                <small>Yas mərasimlərinin tarix və yerlərini bildirin</small>
              </div>
            </div>
          </label>

          {wantsCeremonies && (
            <div className="cp-ceremony-body">
              <div className="cp-ceremony-buttons">
                {Object.entries(availableCeremonies).map(([key, isAvailable]) =>
                  isAvailable && !ceremonies[key].active && (
                    <button
                      key={key}
                      type="button"
                      className="cp-ceremony-add-btn"
                      onClick={() =>
                        setCeremonies(prev => ({
                          ...prev,
                          [key]: { ...prev[key], active: true }
                        }))
                      }
                    >
                      ➕ {
                        key === "threeDay" ? "3-cü gün" :
                          key === "sevenDay" ? "7-ci gün" :
                            key === "fortyDay" ? "40-cı gün" : "İl dönümü"
                      } mərasimi
                    </button>
                  )
                )}
              </div>

              {Object.entries(ceremonies).map(([key, value]) =>
                value.active && (
                  <div key={key} className="cp-ceremony-card">
                    <h4>
                      {key === "threeDay" ? "3-cü gün mərasimi" :
                        key === "sevenDay" ? "7-ci gün mərasimi" :
                          key === "fortyDay" ? "40-cı gün mərasimi" : "İl dönümü mərasimi"}
                    </h4>

                    <label>
                      Mərasim tarixi:
                      <DatePicker
                        selected={value.date}
                        onChange={(date) => handleCeremonyChange(key, 'date', date)}
                        dateFormat="dd-MM-yyyy HH:mm"
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        placeholderText="Tarixi və vaxtı seçin"
                        className="cp-date-input"
                        locale="az"
                        minDate={new Date()}
                      />
                    </label>

                    <label>
                      Məkanın adı:
                      <input
                        type="text"
                        value={value.location}
                        onChange={(e) => handleCeremonyChange(key, 'location', e.target.value)}
                        placeholder="Məsələn: Yas mərasim evi, Məscid adı"
                        maxLength="100"
                      />
                    </label>

                    <label>
                      Xəritə linki (istəyə uyğun):
                      <input
                        type="url"
                        value={value.mapLink}
                        onChange={(e) => handleCeremonyChange(key, 'mapLink', e.target.value)}
                        placeholder="https://maps.google.com/..."
                      />
                    </label>

                    <button
                      type="button"
                      className="cp-ceremony-remove-btn"
                      onClick={() =>
                        setCeremonies(prev => ({
                          ...prev,
                          [key]: { active: false, date: null, location: "", mapLink: "" }
                        }))
                      }
                    >
                      ✖ Mərasimi sil
                    </button>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    )
  );

  const renderErrorSummary = () => (
    Object.keys(formErrors).length > 0 && (
      <div className="cp-form-errors-summary">
        <h4>⚠️ Düzəldilməsi lazım olan sahələr:</h4>
        <ul>
          {Object.entries(formErrors).map(([field, error]) => (
            <li key={field}>{error}</li>
          ))}
        </ul>
      </div>
    )
  );

  // ← DƏYİŞDİ: bütün funksiya yenidir (isFree məntiqi)
  const renderSubmitButton = () => {
    const isFree = isMartyr || !paymentEnabled;

    return (
      <div className="cp-form-actions">
        <button
          type="submit"
          className={`cp-submit-btn ${isSubmitting ? 'cp-loading' : ''} ${isFree ? 'cp-martyr' : 'cp-regular'}`}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="cp-spinner-small"></span>
              Yaradılır...
            </>
          ) : isFree ? (
            <>
              <span>✓</span>
              Paylaş ( Ödənişsiz )
            </>
          ) : (
            <>
              Paylaş (<span>💳</span> {paymentAmount} AZN)
            </>
          )}
        </button>

        {!isFree && (
          <small className="cp-payment-notice">
            💳 Profil yaradıldıqdan sonra təhlükəsiz ödəniş səhifəsinə yönləndiriləcəksiniz
          </small>
        )}
        {isFree && (
          <small className="cp-payment-notice cp-martyr-notice">
            🕊️ Profil yaradıldıqdan sonra admin tərəfindən yoxlanılacaq
          </small>
        )}
      </div>
    );
  };

  return (
    <div className="cp-create-profile">
      <Helmet>
        <title>Yeni Xatirə Yarat – HeyatYolu</title>
        <meta name="description" content="Heyatdan köçmüş sevdikləriniz üçün xatirə profili yaradın. Bioqrafiya, şəkil və əlavə media ilə onların xatirəsini yaşadın." />
        <meta name="keywords" content="xatirə yarat, mərhum, bioqrafiya, şəhid, yaddaş, rəhmətə gedənlər, profil yaratmaq, heyatyolu" />
      </Helmet>

      {renderDraftModal()}

      <header className="cp-header">
        <h1>➕ Yeni Xatirə Yarat</h1>
        <p>Sevdiklərinizin xatirəsini əbədiləşdirin</p>
      </header>

      {isSubmitting && (
        <div className="cp-loading-overlay">
          <div className="cp-loading-content">
            <div className="cp-spinner"></div>
            <p>Profil yaradılır, zəhmət olmasa gözləyin...</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} encType="multipart/form-data" noValidate>
        {renderPersonalInfo()}
        {renderMournersSection()}
        {renderBiographySection()}
        {renderPhotoSection()}
        {renderMediaSection()}
        {renderMartyrSection()}
        {renderCeremoniesSection()}
        {renderErrorSummary()}
        {renderSubmitButton()}
      </form>
    </div>
  );
}

export default CreateProfile;